/**
 * The in-page half of the review chrome.
 *
 * It exists only on the socket transport. When Loupe frames the app, the shell
 * around it owns the toolbar and this file draws nothing - but when the
 * dashboard *opens* the app in its own window, the reviewer is looking at the
 * application and the controls have to be within reach of their eyes, not in
 * another window they would have to alt-tab to.
 *
 * Deliberately one small pill and one small card. This is somebody else's
 * application; the review tool is a guest in it.
 */

import { IGNORE_CLASS } from "./anchor";
import type { Anchor, Mode, NoteKind, ViewState } from "./protocol";
import type { SocketState } from "./transport";

export interface ChromeHandlers {
  onMode(mode: Mode): void;
  onRecord(recording: boolean): void;
  onNote(note: { body: string; kind: NoteKind }): void;
  /** The author saved an edit of an existing entry (trial 2026-09-10). */
  onUpdate(id: string, note: { body: string; kind: NoteKind }): void;
  onDiscard(): void;
  onOpenDashboard(): void;
}

export type { NoteKind };

const KINDS: Array<{ id: NoteKind; label: string }> = [
  { id: "defect", label: "Fehler" },
  { id: "change", label: "Änderung" },
  { id: "question", label: "Frage" },
  { id: "praise", label: "Lob" },
];

const CSS = `
[data-loupe="bar"] {
  position: fixed; bottom: 12px; left: 50%; transform: translateX(-50%);
  z-index: 2147483001; display: flex; align-items: center; gap: 6px;
  background: #1c2229; color: #e8ecf1; border: 1px solid #2c343d;
  border-radius: 22px; padding: 6px 8px; pointer-events: auto;
  font: 13px/1 system-ui, -apple-system, "Segoe UI", sans-serif;
  box-shadow: 0 6px 24px rgba(0,0,0,.35);
  /* A review at 375 px is the point of the viewport control, so the bar has
     to fit inside one rather than run off the edge of it. */
  max-width: calc(100vw - 16px); flex-wrap: wrap; justify-content: center;
}
/* [F-006] V0.4: while the editor is open the selection is PAUSED, and the
   paused state is the one that can be shown. A guard that only swallowed the
   click would leave the reviewer guessing whether picking is still live. */
[data-loupe="bar"][data-editing="true"] [data-loupe="annotate"] {
  opacity: .45; text-decoration: line-through;
}
@media (max-width: 460px) {
  [data-loupe="bar"] .count { display: none; }
  [data-loupe="bar"] button { padding: 6px 9px; }
}
[data-loupe="bar"] button {
  font: inherit; border: 1px solid transparent; border-radius: 16px;
  padding: 6px 12px; background: transparent; color: #9aa7b4; cursor: pointer;
}
[data-loupe="bar"] button.on { background: #4b8fd6; color: #fff; }
[data-loupe="bar"] button:disabled { opacity: .45; cursor: not-allowed; }
[data-loupe="bar"] button.rec { display: flex; align-items: center; gap: 6px; border-color: #2c343d; }
[data-loupe="bar"] button.rec .dot { width: 8px; height: 8px; border-radius: 50%; background: #d43f3a; }
[data-loupe="bar"] button.rec.on { background: #d43f3a; color: #fff; border-color: #d43f3a; }
[data-loupe="bar"] .brand {
  font-weight: 700; letter-spacing: .08em; text-transform: uppercase;
  color: #4b8fd6; padding: 0 6px; cursor: pointer;
}
[data-loupe="bar"] .count { color: #9aa7b4; padding-right: 6px; font-variant-numeric: tabular-nums; }
/* D-024: the socket's state, shown only when it is not connected. */
[data-loupe="bar"] .status { display: none; color: #f0b429; padding: 0 6px; }
[data-loupe="bar"][data-connection="restarting"] .status,
[data-loupe="bar"][data-connection="lost"] .status { display: inline; }

/* The backdrop behind an open editor or reader (root operator, 2026-09-15) -
   the one the frame surface always had. It covers the bar too: while the card
   is open, the card is what there is to act on. */
[data-loupe="backdrop"] {
  position: fixed; inset: 0; z-index: 2147483002;
  background: rgba(0,0,0,.35); pointer-events: auto; cursor: default;
}

[data-loupe="editor"] {
  position: fixed; bottom: 70px; left: 50%; transform: translateX(-50%);
  z-index: 2147483003; width: min(440px, calc(100vw - 32px));
  background: #1c2229; color: #e8ecf1; border: 1px solid #2c343d;
  border-radius: 8px; padding: 14px; pointer-events: auto;
  font: 13px/1.4 system-ui, -apple-system, "Segoe UI", sans-serif;
  box-shadow: 0 10px 40px rgba(0,0,0,.5);
}
[data-loupe="editor"] .context { color: #9aa7b4; font-size: 12px; margin-bottom: 8px; }
[data-loupe="editor"] .notice { color: #f0b429; font-size: 12px; margin-bottom: 8px; }
[data-loupe="editor"] .heading { font-weight: 600; margin-bottom: 6px; }
[data-loupe="editor"] .body { white-space: pre-wrap; margin: 0 0 10px; max-height: 40vh; overflow: auto; }
[data-loupe="editor"] textarea {
  width: 100%; min-height: 76px; resize: vertical; font: inherit;
  background: #14181d; color: #e8ecf1; border: 1px solid #2c343d;
  border-radius: 4px; padding: 8px;
}
[data-loupe="editor"] .kinds { display: flex; gap: 6px; margin: 10px 0; }
[data-loupe="editor"] .kinds button {
  font: inherit; background: transparent; color: #9aa7b4;
  border: 1px solid #2c343d; border-radius: 4px; padding: 5px 10px; cursor: pointer;
}
[data-loupe="editor"] .kinds button.on { background: #4b8fd6; color: #fff; border-color: #4b8fd6; }
[data-loupe="editor"] .actions { display: flex; justify-content: flex-end; gap: 8px; }
[data-loupe="editor"] .actions button {
  font: inherit; border-radius: 4px; padding: 6px 12px; cursor: pointer;
  background: #4b8fd6; color: #fff; border: 1px solid transparent;
}
[data-loupe="editor"] .actions button.ghost { background: transparent; color: #e8ecf1; border-color: #2c343d; }
[data-loupe="editor"] .actions button:disabled { opacity: .45; cursor: not-allowed; }
`;

export function createChrome(handlers: ChromeHandlers) {
  const style = document.createElement("style");
  style.className = IGNORE_CLASS;
  style.textContent = CSS;

  const bar = document.createElement("div");
  bar.className = IGNORE_CLASS;
  bar.setAttribute("data-loupe", "bar");

  const brand = document.createElement("span");
  brand.className = "brand";
  brand.textContent = "Loupe";
  brand.title = "Übersicht öffnen";
  brand.addEventListener("click", handlers.onOpenDashboard);

  const browse = document.createElement("button");
  browse.textContent = "Browsen";
  const annotate = document.createElement("button");
  annotate.textContent = "Kommentieren";
  annotate.setAttribute("data-loupe", "annotate");

  const record = document.createElement("button");
  record.className = "rec";
  const dot = document.createElement("span");
  dot.className = "dot";
  const recordLabel = document.createElement("span");
  recordLabel.textContent = "Aufnahme";
  record.append(dot, recordLabel);

  const count = document.createElement("span");
  count.className = "count";

  const status = document.createElement("span");
  status.className = "status";

  bar.append(brand, browse, annotate, record, count, status);

  let mode: Mode = "browse";
  let recording = false;

  /*
   * A click on the backdrop does nothing and reaches nobody. Leaving is ESC or
   * a button ([F-006] V0.4, AC-093), and the application underneath must not
   * take the click for one of its own - a "click outside" closing its dialog.
   * The SDK's own capture listeners already pass it by: it is ours.
   */
  const backdrop = document.createElement("div");
  backdrop.className = IGNORE_CLASS;
  backdrop.setAttribute("data-loupe", "backdrop");
  for (const type of ["pointerdown", "mousedown", "click"]) {
    backdrop.addEventListener(type, (event) => {
      event.preventDefault();
      event.stopPropagation();
    });
  }

  function paint(): void {
    browse.classList.toggle("on", mode === "browse");
    annotate.classList.toggle("on", mode === "annotate");
    bar.setAttribute("data-editing", String(editor !== null));
    record.classList.toggle("on", recording);
    recordLabel.textContent = recording ? "Stopp" : "Aufnahme";
    // A recording starts from Browsen and keeps it (the SDK's isRecording).
    annotate.disabled = recording;
    annotate.title = recording ? "Während einer Aufnahme bleibt Browsen an." : "";
    record.disabled = !recording && mode !== "browse";
    record.title = record.disabled ? "Eine Aufnahme startet aus Browsen." : "";
  }

  browse.addEventListener("click", () => handlers.onMode("browse"));
  annotate.addEventListener("click", () => handlers.onMode("annotate"));
  record.addEventListener("click", () => handlers.onRecord(!recording));

  let editor: HTMLElement | null = null;
  let releaseEscape: (() => void) | null = null;

  /**
   * Closing is the single place the selection is re-armed ([F-006] V0.4).
   *
   * Every route out of the editor goes through here - ESC, Verwerfen,
   * Speichern - so "the selection is live again" cannot drift out of step
   * with "the editor is gone". That is why `isEditing` reads the editor
   * rather than a flag beside it: a second variable is a second truth.
   */
  function closeEditor(): void {
    releaseEscape?.();
    releaseEscape = null;
    editor?.remove();
    editor = null;
    backdrop.remove();
    paint();
  }

  /** The card goes up with its backdrop - both open paths come through here. */
  function show(card: HTMLElement): void {
    document.body.append(backdrop, card);
    editor = card;
    paint();
  }

  return {
    mount(): void {
      document.head.appendChild(style);
      document.body.appendChild(bar);
      paint();
    },

    setMode(next: Mode): void {
      mode = next;
      paint();
    },

    /**
     * The connection to Loupe ([F-014], D-024). Nothing is shown while it
     * holds; a restart and a lost connection are named differently, because
     * one is back in seconds and the other may not be.
     */
    setConnection(state: SocketState): void {
      bar.setAttribute("data-connection", state);
      status.textContent =
        state === "restarting"
          ? "Loupe startet neu - gleich wieder verbunden"
          : state === "lost"
            ? "Keine Verbindung zu Loupe"
            : "";
    },

    setRecording(next: boolean, events: number): void {
      recording = next;
      paint();
      if (next) recordLabel.textContent = `Stopp (${events})`;
    },

    /**
     * D-003: `shown` is what has a pin on this page, `total` what the connection
     * holds. Since a pin is drawn only where its element is, one number could
     * not say both - "3 Einträge" beside zero markers reads as a bug, and
     * "0 Einträge" hides that the connection has any.
     */
    setCount(shown: number, total: number): void {
      count.textContent =
        shown === total
          ? shown === 1
            ? "1 Eintrag"
            : `${shown} Einträge`
          : `${shown} von ${total} hier`;
    },

    /**
     * The editor - for a new entry, or (trial 2026-09-10) for an existing one
     * of the reviewer's own, prefilled. Saving an existing entry edits it;
     * every other path out is the same as for a new one.
     */
    openEditor(context: {
      anchor: Anchor;
      view: ViewState;
      withRecording: boolean;
      existing?: { id: string; body: string; kind: NoteKind };
      /** What was typed before - a save that failed puts it back ([NF-012]). */
      draft?: { body: string; kind: NoteKind };
      /** Said above the text, e.g. why it is open again. */
      notice?: string;
    }): void {
      closeEditor();
      const card = document.createElement("div");
      card.className = IGNORE_CLASS;
      card.setAttribute("data-loupe", "editor");
      if (context.existing) card.dataset.entry = context.existing.id;

      const where = document.createElement("div");
      where.className = "context";
      const target =
        context.anchor.selector === "body"
          ? "Zur Seite"
          : context.anchor.text || context.anchor.selector;
      where.textContent = context.existing
        ? `${context.existing.id} bearbeiten · ${target}`
        : `${target} · ${context.view.viewport.w} x ${context.view.viewport.h}` +
          (context.withRecording ? " · mit Aufnahme" : "");

      const text = document.createElement("textarea");
      // A form field without id or name is an issue in the reviewed
      // application's DevTools - and this one is ours, not theirs.
      text.name = "loupe-note";
      text.placeholder = "Was ist Ihnen aufgefallen?";
      text.value = context.draft?.body ?? context.existing?.body ?? "";

      const kinds = document.createElement("div");
      kinds.className = "kinds";
      let kind: NoteKind = context.draft?.kind ?? context.existing?.kind ?? "defect";
      const kindButtons = KINDS.map(({ id, label }) => {
        const button = document.createElement("button");
        button.textContent = label;
        button.classList.toggle("on", id === kind);
        button.addEventListener("click", () => {
          kind = id;
          kindButtons.forEach((b, i) => b.classList.toggle("on", KINDS[i].id === kind));
        });
        kinds.appendChild(button);
        return button;
      });

      const actions = document.createElement("div");
      actions.className = "actions";
      const discard = document.createElement("button");
      discard.className = "ghost";
      discard.textContent = "Verwerfen";
      const save = document.createElement("button");
      save.textContent = "Speichern";
      save.disabled = text.value.trim().length === 0;
      actions.append(discard, save);

      text.addEventListener("input", () => {
        save.disabled = text.value.trim().length === 0;
      });
      discard.addEventListener("click", () => {
        closeEditor();
        handlers.onDiscard();
      });
      save.addEventListener("click", () => {
        if (context.existing) handlers.onUpdate(context.existing.id, { body: text.value.trim(), kind });
        else handlers.onNote({ body: text.value.trim(), kind });
        closeEditor();
      });

      // ESC discards, exactly like Verwerfen ([F-006] V0.4, AC-093). Captured,
      // because the application underneath may well bind ESC itself and a
      // review must not depend on whether it does.
      const onEscape = (event: KeyboardEvent): void => {
        if (event.key !== "Escape") return;
        event.preventDefault();
        event.stopPropagation();
        closeEditor();
        handlers.onDiscard();
      };
      window.addEventListener("keydown", onEscape, true);
      releaseEscape = () => window.removeEventListener("keydown", onEscape, true);

      if (context.notice) {
        const notice = document.createElement("div");
        notice.className = "notice";
        notice.setAttribute("role", "alert");
        notice.textContent = context.notice;
        card.append(where, notice, text, kinds, actions);
      } else {
        card.append(where, text, kinds, actions);
      }
      show(card);
      text.focus();
    },

    /**
     * An entry, to read (trial 2026-09-10) - since 2026-09-25 every entry a
     * pin is clicked for, one's own included: *"when I click on it normaly I
     * just want to know what is it - not manipulate it"*. One's own carries
     * `onEdit`, and the card a *Bearbeiten* that opens the editor - editing is
     * a second, deliberate step, as the pencil is in the shell. It counts as
     * "editing" for the selection lock as well, so a click behind it does not
     * anchor anything.
     */
    openReader(entry: { id: string; body: string; kind: NoteKind | null }, onEdit?: () => void): void {
      closeEditor();
      const card = document.createElement("div");
      card.className = IGNORE_CLASS;
      card.setAttribute("data-loupe", "editor");
      card.dataset.entry = entry.id;
      card.dataset.readonly = "true";

      const heading = document.createElement("div");
      heading.className = "heading";
      const label = KINDS.find((k) => k.id === entry.kind)?.label;
      heading.textContent = label ? `${entry.id} · ${label}` : entry.id;

      const body = document.createElement("p");
      body.className = "body";
      body.textContent = entry.body;

      const actions = document.createElement("div");
      actions.className = "actions";
      const close = document.createElement("button");
      close.className = "ghost";
      close.textContent = "Schließen";
      close.addEventListener("click", closeEditor);
      if (onEdit) {
        const edit = document.createElement("button");
        edit.textContent = "Bearbeiten";
        edit.setAttribute("data-loupe", "reader-edit");
        edit.addEventListener("click", () => {
          closeEditor();
          onEdit();
        });
        actions.append(close, edit);
      } else {
        actions.append(close);
      }

      const onEscape = (event: KeyboardEvent): void => {
        if (event.key !== "Escape") return;
        event.preventDefault();
        event.stopPropagation();
        closeEditor();
      };
      window.addEventListener("keydown", onEscape, true);
      releaseEscape = () => window.removeEventListener("keydown", onEscape, true);

      card.append(heading, body, actions);
      show(card);
    },

    /** Is the editor open - and therefore the selection paused? ([F-006]) */
    isEditing: () => editor !== null,

    closeEditor,
  };
}
